"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import { toast } from "sonner"
import { Plus, Trash2, Baby, GraduationCap, Save, X, Pencil } from "@/lib/icons"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import {
  getLeadChildren,
  createLeadChild,
  updateLeadChild,
  deleteLeadChild,
} from "@/lib/supabase"
import { NIVEL_OPTIONS as NIVEL_OPTIONS_BASE } from "@/lib/educational-levels"

const NIVEL_OPTIONS = [
  { value: "", label: "Sin nivel" },
  ...NIVEL_OPTIONS_BASE,
]

const inputCls =
  "h-8 text-xs px-2.5 rounded-lg border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-950/50 focus:ring-2 focus:ring-rose-500/15 focus:border-rose-300"
const selectCls =
  "h-8 w-full rounded-lg border border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-950/50 px-2 text-xs"
const labelCls =
  "text-[10px] uppercase tracking-wider font-semibold text-slate-500/80 dark:text-slate-400/80 mb-1 flex items-center gap-1.5"

const EMPTY = { nombre: "", nivel_educativo: "", grado_especifico: "", edad: "", sexo: "" }

const SEXO_OPTIONS = [
  { value: "", label: "—" },
  { value: "nino", label: "Niño" },
  { value: "nina", label: "Niña" },
]

export function LeadChildrenSection({ leadId, onChange }) {
  const [children, setChildren] = useState([])
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState(EMPTY)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState(EMPTY)

  // Ref estable para el callback — evita loop cuando el parent pasa arrow function inline
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const reload = useCallback(async () => {
    if (!leadId) return
    setLoading(true)
    const rows = await getLeadChildren(leadId)
    setChildren(rows)
    onChangeRef.current?.(rows)
    setLoading(false)
  }, [leadId])

  useEffect(() => {
    reload()
  }, [reload])

  const handleAdd = async () => {
    if (!draft.nombre.trim()) {
      toast.error("El nombre es obligatorio")
      return
    }
    setAdding(true)
    const { error } = await createLeadChild(leadId, {
      ...draft,
      orden: children.length,
    })
    setAdding(false)
    if (error) {
      toast.error("No se pudo agregar", { description: error.message })
      return
    }
    setDraft(EMPTY)
    toast.success("Hijo agregado")
    reload()
  }

  const startEdit = (child) => {
    setEditingId(child.id)
    setEditDraft({
      nombre: child.nombre || "",
      nivel_educativo: child.nivel_educativo || "",
      grado_especifico: child.grado_especifico || "",
      edad: child.edad ?? "",
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditDraft(EMPTY)
  }

  const handleUpdate = async (id) => {
    if (!editDraft.nombre.trim()) {
      toast.error("El nombre es obligatorio")
      return
    }
    const { error } = await updateLeadChild(id, editDraft)
    if (error) {
      toast.error("No se pudo actualizar", { description: error.message })
      return
    }
    cancelEdit()
    toast.success("Hijo actualizado")
    reload()
  }

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este hijo?")) return
    const { error } = await deleteLeadChild(id)
    if (error) {
      toast.error("No se pudo eliminar", { description: error.message })
      return
    }
    toast.success("Hijo eliminado")
    reload()
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
          <Baby className="h-3 w-3" /> Hijos registrados
        </p>
        <span className="text-[10px] font-semibold text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 px-2 py-0.5 rounded-full">
          {children.length} {children.length === 1 ? "hijo" : "hijos"}
        </span>
      </div>

      {/* Lista existente */}
      {loading && <p className="text-[11px] text-slate-400">Cargando…</p>}

      {!loading && children.length === 0 && (
        <p className="text-[11px] text-slate-400 italic">
          Aún no hay hijos registrados. Agrega uno abajo.
        </p>
      )}

      <div className="space-y-2">
        {children.map((child) => {
          const isEditing = editingId === child.id
          if (isEditing) {
            return (
              <div
                key={child.id}
                className="rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50/40 dark:bg-rose-950/20 p-2.5 space-y-2"
              >
                <Input
                  value={editDraft.nombre}
                  onChange={(e) =>
                    setEditDraft((d) => ({ ...d, nombre: e.target.value }))
                  }
                  className={inputCls}
                  placeholder="Nombre del hijo"
                />
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={editDraft.nivel_educativo}
                    onChange={(e) =>
                      setEditDraft((d) => ({
                        ...d,
                        nivel_educativo: e.target.value,
                      }))
                    }
                    className={selectCls}
                  >
                    {NIVEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={editDraft.sexo}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, sexo: e.target.value }))
                    }
                    className={selectCls}
                  >
                    {SEXO_OPTIONS.map((o) => (
                      <option key={o.value || "none"} value={o.value}>
                        {o.label === "—" ? "Sexo..." : o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={editDraft.grado_especifico}
                    onChange={(e) =>
                      setEditDraft((d) => ({
                        ...d,
                        grado_especifico: e.target.value,
                      }))
                    }
                    className={inputCls}
                    placeholder="Grado"
                  />
                  <Input
                    type="number"
                    value={editDraft.edad}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, edad: e.target.value }))
                    }
                    className={inputCls}
                    placeholder="Edad"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[11px]"
                    onClick={cancelEdit}
                  >
                    <X className="h-3 w-3 mr-1" /> Cancelar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 text-[11px] bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={() => handleUpdate(child.id)}
                  >
                    <Save className="h-3 w-3 mr-1" /> Guardar
                  </Button>
                </div>
              </div>
            )
          }

          const nivelLabel =
            NIVEL_OPTIONS.find((o) => o.value === child.nivel_educativo)?.label || null

          return (
            <div
              key={child.id}
              className="group flex items-center gap-2 rounded-lg border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 px-2.5 py-2 hover:border-rose-200 dark:hover:border-rose-900/50 transition-colors"
            >
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                  child.sexo === "nina"
                    ? "bg-pink-100 dark:bg-pink-950/40 text-pink-700 dark:text-pink-300"
                    : child.sexo === "nino"
                      ? "bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300"
                      : "bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300"
                }`}
              >
                {(child.nombre || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">
                  {child.nombre}
                  {child.sexo === "nino" && <span className="ml-1 text-[9px] text-sky-600 dark:text-sky-400">♂</span>}
                  {child.sexo === "nina" && <span className="ml-1 text-[9px] text-pink-600 dark:text-pink-400">♀</span>}
                </p>
                <p className="text-[10px] text-slate-500 truncate flex items-center gap-1">
                  {nivelLabel && (
                    <>
                      <GraduationCap className="h-2.5 w-2.5" />
                      <span>{nivelLabel}</span>
                    </>
                  )}
                  {child.grado_especifico && <span>· {child.grado_especifico}</span>}
                  {child.edad != null && <span>· {child.edad} años</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => startEdit(child)}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 opacity-0 group-hover:opacity-100 transition-all"
                title="Editar"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(child.id)}
                className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 opacity-0 group-hover:opacity-100 transition-all"
                title="Eliminar"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )
        })}
      </div>

      {/* Agregar nuevo */}
      <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/30 dark:bg-slate-950/30 p-2.5 space-y-2">
        <Label className={labelCls}>
          <Plus className="h-3 w-3" /> Agregar hijo
        </Label>
        <Input
          value={draft.nombre}
          onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))}
          className={inputCls}
          placeholder="Nombre del hijo *"
        />
        <div className="grid grid-cols-2 gap-2">
          <select
            value={draft.nivel_educativo}
            onChange={(e) =>
              setDraft((d) => ({ ...d, nivel_educativo: e.target.value }))
            }
            className={selectCls}
          >
            {NIVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={draft.sexo}
            onChange={(e) => setDraft((d) => ({ ...d, sexo: e.target.value }))}
            className={selectCls}
          >
            {SEXO_OPTIONS.map((o) => (
              <option key={o.value || "none"} value={o.value}>
                {o.label === "—" ? "Sexo..." : o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={draft.grado_especifico}
            onChange={(e) =>
              setDraft((d) => ({ ...d, grado_especifico: e.target.value }))
            }
            className={inputCls}
            placeholder="Grado"
          />
          <Input
            type="number"
            value={draft.edad}
            onChange={(e) => setDraft((d) => ({ ...d, edad: e.target.value }))}
            className={inputCls}
            placeholder="Edad"
            min="0"
            max="25"
          />
        </div>
        <Button
          type="button"
          size="sm"
          className="w-full h-8 text-[11px] bg-rose-600 hover:bg-rose-700 text-white"
          disabled={adding || !draft.nombre.trim()}
          onClick={handleAdd}
        >
          <Plus className="h-3 w-3 mr-1" />
          {adding ? "Agregando..." : "Agregar hijo"}
        </Button>
      </div>
    </div>
  )
}
